import {
  Button,
  ButtonColor,
  ButtonWidth,
  FontWeight,
  formatPrice,
  IconChevronDown,
  Modal,
  ModalContent,
  type ModalHook,
  useControlledModalState,
} from '@leland-dev/leland-ui-library';
import { AnimatePresence, domMax, LazyMotion, m } from 'framer-motion';
import { differenceBy } from 'lodash-es';
import React, {
  type FC,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useState,
} from 'react';
import {
  type SubmitHandler,
  useForm,
  type UseFormGetValues,
} from 'react-hook-form';

import { type Scalars } from '../../__generated-gql-types__/globalTypes';
import { useDebouncedCallback } from '../../utils/hooks/useDebounce';
import { type OfferingPackagesFilterInputs } from '../../utils/package';
import { type SchoolFilterHookOutput } from '../filters/SchoolFilters';
import Input from '../inputs/Input';
import { MultiSchoolTypeahead } from '../typeahead/SchoolTypeahead';

import {
  type OfferingPackagesFiltersModalCoachingFocusFragment,
  type OfferingPackagesFiltersModalSchoolFragment,
  useOfferingPackagesFiltersModalGetTotalPackagesLazyQuery,
} from './__generated-gql-types__/OfferingPackagesFiltersModal.generated';

export const MIN_PRICE = 1;
export const MIN_PRICE_WARNING = `Price must be a minimum of ${formatPrice(MIN_PRICE * 100)}`;

const REGISTER_OPTIONS = {
  PRICE_FILTER_START: {
    valueAsNumber: true,
    min: {
      value: MIN_PRICE,
      message: MIN_PRICE_WARNING,
    },
  },
  PRICE_FILTER_END: (
    getValues: UseFormGetValues<OfferingPackagesFilterInputs>,
  ) => ({
    valueAsNumber: true,
    min: {
      value: MIN_PRICE,
      message: MIN_PRICE_WARNING,
    },
    validate: () => {
      const minPrice = getValues('priceFilter.start');
      const maxPrice = getValues('priceFilter.end');

      if (minPrice && maxPrice && maxPrice < minPrice) {
        return `Price must be greater than or equal to ${minPrice}`;
      }
    },
  }),
};

interface OfferingPackagesFiltersModalHookProps {
  categoryUrn: Scalars['CategoryUrn']['output'];
  coachingFocuses?: Nullable<
    OfferingPackagesFiltersModalCoachingFocusFragment[]
  >;
  comprehensiveFocuses?: Nullable<Array<Scalars['CoachingFocusUrn']['output']>>;
  defaultValues: Nullable<OfferingPackagesFilterInputs>;
  defaultTotalOfferingPackages: number;
  isDesktop?: boolean;
  onRefetchPackages: (variables: OfferingPackagesFilterInputs) => Promise<void>;
  schoolFilters: SchoolFilterHookOutput;
}

interface OfferingPackagesFiltersModalProps
  extends OfferingPackagesFiltersModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const OfferingPackagesFiltersModalContent: FC<
  OfferingPackagesFiltersModalProps
> = ({
  open,
  setOpen,
  categoryUrn,
  coachingFocuses,
  comprehensiveFocuses,
  defaultValues,
  defaultTotalOfferingPackages,
  isDesktop,
  onRefetchPackages,
  schoolFilters: { selectedSchools, addSchoolFilters, removeSchoolFilters },
}) => {
  const [
    getTotalPackages,
    { data: offeringPackagesTotal, loading: offeringPackagesTotalLoading },
  ] = useOfferingPackagesFiltersModalGetTotalPackagesLazyQuery();

  const defaultValuesCleaned = defaultValues
    ? {
        ...defaultValues,
        priceFilter: {
          start: defaultValues?.priceFilter?.start
            ? defaultValues.priceFilter.start / 100
            : null,
          end: defaultValues?.priceFilter?.end
            ? defaultValues.priceFilter.end / 100
            : null,
        },
      }
    : {};

  const {
    register,
    setValue,
    getValues,
    watch,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<OfferingPackagesFilterInputs>({
    defaultValues: defaultValuesCleaned,
  });

  register('coachingFocusListFilter');

  const watchPriceFilter = watch('priceFilter');
  const watchCoachingFocusListFilter = watch('coachingFocusListFilter');

  const [schoolTypeaheadValues, setSchoolTypeaheadValues] =
    useState<OfferingPackagesFiltersModalSchoolFragment[]>(selectedSchools);

  const totalOfferingPackages =
    offeringPackagesTotal?.searchPackages.total ?? defaultTotalOfferingPackages;

  const comprehensivePackageFilters = comprehensiveFocuses;

  const noFiltersApplied =
    (watchPriceFilter?.start ?? 0) === 0 &&
    (watchPriceFilter?.end ?? 0) === 0 &&
    (watchCoachingFocusListFilter ?? []).length == 0;

  const getPriceFilterCleaned = useCallback(() => {
    const priceFilter = getValues('priceFilter');
    const priceFilterCleaned =
      !priceFilter?.start && !priceFilter?.end
        ? null
        : {
            start: priceFilter?.start ? priceFilter.start * 100 : null,
            end: priceFilter?.end ? priceFilter.end * 100 : null,
          };

    return priceFilterCleaned;
  }, [getValues]);

  const getTotalPackagesCallback = useCallback(
    () =>
      getTotalPackages({
        variables: {
          categoryUrn: categoryUrn,
          priceFilter: getPriceFilterCleaned(),
          coachingFocusListFilter: getValues('coachingFocusListFilter'),
        },
      }),
    [categoryUrn, getValues, getTotalPackages, getPriceFilterCleaned],
  );

  const debouncedGetTotalPackages = useDebouncedCallback(
    getTotalPackagesCallback,
    500,
    true,
  );

  const handleFocusClick = async (
    focus: Scalars['CoachingFocusUrn']['output'],
    isSelected: boolean,
  ) => {
    let newCoachingFocusListFilter: Array<
      Scalars['CoachingFocusUrn']['output']
    >;

    if (isSelected) {
      newCoachingFocusListFilter =
        watchCoachingFocusListFilter?.filter(
          (selectedFocus) => selectedFocus !== focus,
        ) ?? [];
    } else {
      newCoachingFocusListFilter = [
        ...(watchCoachingFocusListFilter ?? []),
        focus,
      ];
    }

    setValue('coachingFocusListFilter', newCoachingFocusListFilter);
    await debouncedGetTotalPackages();
  };

  const selectComprehensivePackageFilters = async () => {
    setValue('coachingFocusListFilter', comprehensivePackageFilters);
    await debouncedGetTotalPackages();
  };

  const applyFilters = async () => {
    const addedItem = differenceBy(
      schoolTypeaheadValues,
      selectedSchools,
      'id',
    )[0];
    if (addedItem) {
      await addSchoolFilters({ schools: [addedItem] }, true);
    }

    const removedItem = differenceBy(
      selectedSchools,
      schoolTypeaheadValues,
      'id',
    )[0];
    if (removedItem) {
      removeSchoolFilters([removedItem.id], true);
    }
    await onRefetchPackages({
      priceFilter: getPriceFilterCleaned(),
      coachingFocusListFilter: getValues('coachingFocusListFilter'),
      schoolsSort: schoolTypeaheadValues.map((v) => v.id),
    });
  };

  const clearFilters = async () => {
    reset({ priceFilter: null, coachingFocusListFilter: null });
    await debouncedGetTotalPackages();
    await applyFilters();
  };

  const onSubmitWrapper: SubmitHandler<
    OfferingPackagesFilterInputs
  > = async () => {
    try {
      await applyFilters();
      setOpen(false);
    } catch (e) {
      console.warn(e);
    }
  };

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalContent
        header="Filters"
        footer={
          <div className="flex w-full items-center justify-between space-x-2 p-2">
            <Button
              label="Clear all"
              buttonColor={ButtonColor.WHITE}
              width={ButtonWidth.FULL_MOBILE}
              disabled={noFiltersApplied}
              onClick={clearFilters}
            />
            <Button
              form="offering-packages-filters-form"
              type="submit"
              label={`Show ${
                offeringPackagesTotalLoading ? '' : totalOfferingPackages
              } packages`}
              buttonColor={ButtonColor.PRIMARY}
              width={ButtonWidth.FULL_MOBILE}
              disabled={offeringPackagesTotalLoading || isSubmitting}
            />
          </div>
        }
      >
        <LazyMotion strict features={domMax}>
          <form
            id="offering-packages-filters-form"
            className="p-6"
            onSubmit={handleSubmit(onSubmitWrapper)}
          >
            <Filter
              label="Price Range"
              subLabel="Adjust the range below to find a package that fits your budget."
              collapsible={!isDesktop}
            >
              <div className="mt-5 flex items-start justify-center">
                <Input
                  className="w-full"
                  label="Minimum Price"
                  labelIsHidden
                  placeholder="Min"
                  subtlePrefix="$"
                  postfix="USD"
                  type="number"
                  register={register(
                    'priceFilter.start',
                    REGISTER_OPTIONS.PRICE_FILTER_START,
                  )}
                  onChange={debouncedGetTotalPackages}
                  error={errors.priceFilter?.start}
                />
                <p className="p-3 text-leland-gray-extra-light">–</p>
                <Input
                  className="w-full"
                  label="Maximum Price"
                  labelIsHidden
                  placeholder="Max"
                  subtlePrefix="$"
                  postfix="USD"
                  type="number"
                  register={register(
                    'priceFilter.end',
                    REGISTER_OPTIONS.PRICE_FILTER_END(getValues),
                  )}
                  onChange={debouncedGetTotalPackages}
                  error={errors.priceFilter?.end}
                />
              </div>
            </Filter>
            <hr className="my-6 lg:my-8" />
            <Filter label="Target schools" subLabel="" collapsible={!isDesktop}>
              <MultiSchoolTypeahead
                className="mt-4"
                values={schoolTypeaheadValues}
                onChange={(
                  values: OfferingPackagesFiltersModalSchoolFragment[],
                ) => {
                  setSchoolTypeaheadValues(values);
                }}
                label="Target schools"
                labelIsHidden
              />
            </Filter>
            {coachingFocuses?.length ? (
              <>
                <hr className="my-6 lg:my-8" />
                <Filter
                  label="Services"
                  subLabel={
                    comprehensivePackageFilters ? (
                      <>
                        If you&rsquo;re looking for a comprehensive package,{' '}
                        <button
                          type="button"
                          className="underline"
                          onClick={selectComprehensivePackageFilters}
                        >
                          click here.
                        </button>
                      </>
                    ) : null
                  }
                  collapsible={!isDesktop}
                >
                  <div className="mt-4 flex flex-wrap gap-2">
                    {coachingFocuses?.map((focus) => {
                      const isSelected =
                        !!watchCoachingFocusListFilter?.includes(focus.id);

                      return (
                        <Button
                          key={focus.id}
                          buttonColor={
                            isSelected ? ButtonColor.BLACK : ButtonColor.WHITE
                          }
                          onClick={() => handleFocusClick(focus.id, isSelected)}
                          label={focus.name}
                          rounded
                          fontWeight={FontWeight.NORMAL}
                        />
                      );
                    })}
                  </div>
                </Filter>
              </>
            ) : null}
          </form>
        </LazyMotion>
      </ModalContent>
    </Modal>
  );
};

type FilterProps = PropsWithChildren<{
  label: string;
  subLabel: Possible<ReactNode>;
  collapsible?: boolean;
}>;

const Filter: React.FC<FilterProps> = ({
  label,
  subLabel,
  collapsible,
  children,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <>
      <div className="flex items-center justify-between space-x-4">
        <h2 className="text-base font-medium text-leland-gray-dark lg:text-lg">
          {label}
        </h2>
        {collapsible ? (
          <button
            type="button"
            onClick={() => setCollapsed((prevState) => !prevState)}
          >
            <IconChevronDown
              className={`size-4 text-leland-gray-light transition-transform ${
                collapsed ? '' : 'rotate-180'
              }`}
            />
          </button>
        ) : null}
      </div>
      {subLabel ? (
        <p className="mt-0.5 text-base font-normal text-leland-gray-light">
          {subLabel}
        </p>
      ) : null}
      <AnimatePresence initial={false}>
        {collapsed ? null : (
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {children}
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const useOfferingPackagesFiltersModal: ModalHook<
  OfferingPackagesFiltersModalHookProps
> = () => {
  const [open, setOpen] = useControlledModalState();
  const OfferingPackagesFiltersModal: FC<
    OfferingPackagesFiltersModalHookProps
  > = (props) => {
    return open ? (
      <OfferingPackagesFiltersModalContent
        {...props}
        open={open}
        setOpen={setOpen}
      />
    ) : null;
  };
  return { open, setOpen, Modal: OfferingPackagesFiltersModal };
};
